import { useMemo } from "react";
import { Grid, CardContent, Typography } from "@mui/material";
import { IFullFlight } from "@vmosolutions/oc-interfaces";

import { FormHeader } from "../helpers";
import { DelaysForm } from "src/pages/FlightEditor/DelaysForm/DelaysForm";
import moment from "moment-timezone";
import { minutes2HrsMin } from "src/utils";
import WarningIcon from "@mui/icons-material/Warning";

interface IDelaysTabProps {
  fullFlight: IFullFlight; // Id of the flight to retrieve and edit if provided.
  setLoadedFlight: (flightValues: IFullFlight) => void;
  updateTabState: (dirtyStatus: boolean) => void; // update parent with dirty status.
  mosaicId: string;
  setAPICallStatus: (statusMessage: string) => void;
}

/**
 * Flight Delays Tab
 * @param props -
 */
export const DelaysTab = (props: IDelaysTabProps): JSX.Element => {
  const { fullFlight, setLoadedFlight, updateTabState, mosaicId, setAPICallStatus } = props;
  const { _id, delays, actualOut, actualIn, scheduledOut, scheduledIn, origin, destination } = fullFlight ?? {};

  const airportIdents: string[] = [origin, destination];

  // Calculate signed offsets in minutes - treat only positive values as delays.
  const departureDelayMinutes = useMemo(() => {
    if (!actualOut || !scheduledOut) return 0;
    const diff = moment(actualOut).diff(moment(scheduledOut), "minutes");
    return diff > 0 ? diff : 0; // only count late departures
  }, [actualOut, scheduledOut]);

  const arrivalDelayMinutes = useMemo(() => {
    if (!actualIn || !scheduledIn) return 0;
    const diff = moment(actualIn).diff(moment(scheduledIn), "minutes");
    return diff > 0 ? diff : 0; // only count late arrivals
  }, [actualIn, scheduledIn]);

  // Only show minutes when there is an actual delay, otherwise show a dash.
  const subtitle = `(Departure Delay: ${
    departureDelayMinutes > 0 ? minutes2HrsMin(departureDelayMinutes) : "-"
  } - Arrival Delay: ${arrivalDelayMinutes > 0 ? minutes2HrsMin(arrivalDelayMinutes) : "-"})`;

  const depDelays = useMemo(() => delays?.filter((delay) => delay?.onDeparture), [delays]);
  const arrDelays = useMemo(() => delays?.filter((delay) => !delay?.onDeparture), [delays]);
  const departureTimeWarning = useMemo(() => {
    let warning = "";
    depDelays.forEach((delay) => {
      if (delay.duration > departureDelayMinutes) {
        warning = "Duration set on departure delay detail(s) exceed current departure delay on flight.";
      }
    });
    return warning;
  }, [depDelays, departureDelayMinutes]);

  const arrivalTimeWarning = useMemo(() => {
    let warning = "";
    arrDelays.forEach((delay) => {
      if (delay.duration > arrivalDelayMinutes) {
        warning = "Duration set on arrival delay detail(s) exceed current arrival delay on flight.";
      }
    });
    return warning;
  }, [arrDelays, arrivalDelayMinutes]);

  return (
    <CardContent id={`${mosaicId}-delays-editor`} className="form_card delays">
      <Grid container direction="column">
        <FormHeader title="Delay Details" subtitle={subtitle} />
        {(departureTimeWarning || arrivalTimeWarning) && (
          <Grid container className="delay_time_warning" alignItems="center" wrap="nowrap" style={{ marginLeft: 10 }}>
            <WarningIcon style={{ color: "orange", width: 14 }} />
            <Typography variant="body2" color="secondary" style={{ fontWeight: 300, marginLeft: 5 }}>
              {`${departureTimeWarning} ${arrivalTimeWarning}`}
            </Typography>
          </Grid>
        )}
        {fullFlight ? (
          <DelaysForm
            flightId={_id}
            delays={delays}
            actualOut={actualOut?.valueOf()}
            actualIn={actualIn?.valueOf()}
            scheduledOut={scheduledOut?.valueOf()}
            scheduledIn={scheduledIn?.valueOf()}
            departDelayDuration={departureDelayMinutes}
            arrivalDelayDurtaion={arrivalDelayMinutes}
            airportIdent={airportIdents}
            windowId={mosaicId}
            setLoadedFlight={setLoadedFlight}
            updateTabState={updateTabState}
          />
        ) : null}
      </Grid>
    </CardContent>
  );
};
